import {
  type EKycVerificationStatus,
  type ExamineeRegistrationStatus,
  type ExamineeRegistrationType,
} from '@exam-center/graphql';
import { Box, EllipsisText, Text, type GridColDef } from '@shared/components';

import { ExamineeKycIcon } from '../../components/examinee-kyc-icon';
import { RegistrationStatusLabel } from '../../components/registration-status-label';
import { RegistrationTypeLabel } from '../../components/registration-type-label';

import MoreActionsButton from './more-actions-button';

export interface RegistrationExamineeColumn {
  id: string;
  examEventStartedAt: Date;
  examEventExamineeId: string;
  seatNumber: number;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  examCourseCode: string;
  eKycStatus: EKycVerificationStatus;
  registrationStatus: ExamineeRegistrationStatus;
  registrationType: ExamineeRegistrationType | '-';
}

export const columns: GridColDef<RegistrationExamineeColumn>[] = [
  {
    field: 'seatNumber',
    headerName: 'เลขที่นั่ง',
    width: 78,
    sortable: false,
    align: 'center',
    renderCell: ({ row }) => {
      const { seatNumber } = row;
      return <>{seatNumber ?? '-'}</>;
    },
  },
  {
    field: 'fullName',
    headerName: 'ชื่อผู้เข้าสอบ',
    flex: 1,
    minWidth: 240,
    sortable: false,
    renderCell: ({ row }) => {
      const { title, firstName, middleName, lastName } = row;
      return [title, firstName, middleName, lastName].filter(Boolean).join(' ');
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 240,
    sortable: false,
    renderCell: ({ row }) => {
      const { email } = row;
      return <EllipsisText>{email ?? '-'}</EllipsisText>;
    },
  },
  {
    field: 'examCourseCode',
    headerName: 'หลักสูตร',
    width: 120,
    sortable: false,
  },
  {
    field: 'eKycStatus',
    headerName: 'e-KYC',
    width: 100,
    sortable: false,
    renderCell: ({ row }) => {
      const { eKycStatus } = row;
      return <ExamineeKycIcon status={eKycStatus} />;
    },
  },
  {
    field: 'registrationStatus',
    headerName: 'สถานะการลงทะเบียน',
    width: 200,
    sortable: false,
    renderCell: ({ row }) => {
      const { registrationStatus } = row;
      return <RegistrationStatusLabel variant={registrationStatus} />;
    },
  },
  {
    field: 'registrationType',
    headerName: 'ประเภทการลงทะเบียน',
    width: 200,
    sortable: false,
    renderCell: ({ row }) => {
      const { registrationType } = row;
      if (registrationType === '-') {
        return '-';
      }
      return <RegistrationTypeLabel variant={registrationType} />;
    },
  },
  {
    field: 'moreActions',
    headerName: '',
    width: 68,
    sortable: false,
    renderCell: ({ row }) => {
      const examEventExamineeId = row.examEventExamineeId || '';

      return (
        <MoreActionsButton
          examEventExamineeId={examEventExamineeId}
          status={row.registrationStatus}
          examEventStartedAt={row.examEventStartedAt}
          eKycStatus={row.eKycStatus}
        />
      );
    },
  },
];

export const mobileVariantColumns: GridColDef<RegistrationExamineeColumn>[] = [
  {
    field: 'examineeInfo',
    headerName: 'ข้อมูลผู้เข้าสอบ',
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const { title, firstName, lastName, email, registrationStatus, eKycStatus } = row;
      const examEventExamineeId = row.examEventExamineeId || '';
      return (
        <Box
          sx={{ display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: '8px' }}>
            <Text sx={{ fontWeight: 600 }}>{[title, firstName, lastName].join(' ')}</Text>
            <Text>{email ?? '-'}</Text>
            <Text sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              e-KYC: <ExamineeKycIcon status={eKycStatus} />
            </Text>
            <RegistrationStatusLabel variant={registrationStatus} />
          </Box>
          <Box>
            <MoreActionsButton
              examEventExamineeId={examEventExamineeId}
              status={row.registrationStatus}
              examEventStartedAt={row.examEventStartedAt}
              eKycStatus={row.eKycStatus}
            />
          </Box>
        </Box>
      );
    },
  },
];
